// AuditStreamWidget — Control Plane Observability layer.
//
// Polls `GET /api/audit/stream` every 15s and renders the last N
// governed events (financial reversals, journal-emitting posts,
// permission changes, etc.) for the active tenant.
//
// Architecture:
//   • Backend chooses which tags to expose (no client-side allowlist).
//   • Backend computes severity (critical / high / info).
//   • Backend computes the entity_link (which page the row navigates to).
//   • This component renders only — no inference, no derived state.
//
// Two views:
//   • "stream" (default) — chronological list of last 10 events.
//   • "groups"           — same events grouped by (entity_type, entity_id),
//                          to spot "every change touching INV-00018" pattern.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import {
  Activity, AlertTriangle, ArrowRight, ChevronDown, ChevronRight,
  RefreshCw, FileText, CreditCard, Users, FolderKanban, ShieldCheck,
} from 'lucide-react';

const SEVERITY = {
  critical: {
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
    dot: 'bg-rose-500',
    label: 'حرج',
  },
  high: {
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    label: 'مالي',
  },
  info: {
    badge: 'bg-sky-100 text-sky-800 border-sky-200',
    dot: 'bg-sky-500',
    label: 'إداري',
  },
};

const ENTITY_ICONS = {
  invoice: FileText,
  payment: CreditCard,
  customer: Users,
  project: FolderKanban,
  user: ShieldCheck,
  credit_note: FileText,
  journal: FileText,
  bank_account: Activity,
  bank_transaction: Activity,
  petty_cash_fund: CreditCard,
  petty_cash_transaction: CreditCard,
};

// Arabic-friendly labels for the audit tags.
const TAG_LABELS = {
  INVOICE_POST: 'ترحيل فاتورة',
  INVOICE_APPROVE: 'اعتماد فاتورة',
  INVOICE_REJECT: 'رفض فاتورة',
  INVOICE_AMEND_DRAFT: 'مسوّدة تعديل فاتورة',
  INVOICE_AMEND_POST: 'ترحيل تعديل فاتورة',
  CREDIT_NOTE_AMEND: 'مسوّدة تعديل إشعار دائن',
  CREDIT_NOTE_AMEND_POST: 'ترحيل تعديل إشعار دائن',
  JOURNAL_AMEND: 'مسوّدة تعديل قيد محاسبي',
  JOURNAL_AMEND_POST: 'ترحيل تعديل قيد محاسبي',
  RECONCILIATION_MATCH: 'محاولة مطابقة بنكية (مسوّدة)',
  RECONCILIATION_MATCH_POST: 'ترحيل مطابقة بنكية',
  BANK_LEDGER_TOUCH: 'تأثير على دفتر الحساب البنكي',
  CASH_LEDGER_TOUCH: 'تأثير على دفتر صندوق النقد',
  PETTY_CASH_TX: 'حركة صندوق نقدي',
  PETTY_CASH_TX_DELETE: 'حذف حركة صندوق نقدي',
  PAYMENT_CREATE: 'تسجيل دفعة',
  FINANCIAL_REVERSAL: 'استرداد مالي',
  ENTITY_HARD_DELETE: 'حذف نهائي',
  PERMISSION_CHANGED: 'تعديل صلاحيات',
  CAPABILITY_GRANTED: 'منح صلاحية مخصّصة',
  CAPABILITY_REVOKED: 'إلغاء صلاحية مخصّصة',
};

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'الآن';
    if (mins < 60) return `قبل ${mins} د`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `قبل ${hrs} س`;
    return d.toLocaleString('ar-SA', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function EventRow({ ev, onOpen }) {
  const sev = SEVERITY[ev.severity] || SEVERITY.info;
  const EntityIcon = ENTITY_ICONS[ev.entity_type] || Activity;
  const label = TAG_LABELS[ev.action] || ev.action;
  const meta = ev.metadata || {};
  return (
    <li
      data-testid={`audit-event-${ev.id}`}
      className="flex items-start gap-3 px-3 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
      onClick={() => onOpen?.(ev)}
    >
      <div className={`mt-1 w-2 h-2 rounded-full ${sev.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${sev.badge}`}>
            {sev.label}
          </span>
          <EntityIcon className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-semibold text-sm text-slate-800 truncate">{label}</span>
          <span className="text-xs text-slate-400 ms-auto">{fmtTime(ev.ts)}</span>
        </div>
        <div className="text-xs text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
          <span className="text-slate-500">{ev.user_name}</span>
          {ev.entity_id && (
            <>
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">
                {String(ev.entity_id).slice(0, 16)}
                {String(ev.entity_id).length > 16 ? '…' : ''}
              </span>
            </>
          )}
          {meta.from_status && meta.to_status && (
            <span className="text-amber-700">
              {meta.from_status} <ArrowRight className="inline w-3 h-3" /> {meta.to_status}
            </span>
          )}
          {typeof meta.requested === 'number' && (
            <span className="text-rose-700">−{Number(meta.requested).toLocaleString('ar-SA')} SAR</span>
          )}
        </div>
      </div>
    </li>
  );
}

export default function AuditStreamWidget({ pollMs = 15000 }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('stream');     // 'stream' | 'groups'
  const [filter, setFilter] = useState('all');    // 'all' | 'critical' | 'high'
  const [expandedGroups, setExpandedGroups] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/audit/stream', { params: { limit: 10 } });
      setEvents(res.data?.events || []);
      setGroups(res.data?.groups || []);
    } catch (err) {
      // Silent fail — widget is non-blocking.
      console.warn('audit stream fetch failed', err?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (pollMs > 0) {
      const id = setInterval(fetchData, pollMs);
      return () => clearInterval(id);
    }
  }, [fetchData, pollMs, refreshTick]);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => e.severity === filter);
  }, [events, filter]);

  const openEntity = useCallback((ev) => {
    if (ev?.entity_link) navigate(ev.entity_link);
  }, [navigate]);

  const counts = useMemo(() => ({
    all: events.length,
    critical: events.filter((e) => e.severity === 'critical').length,
    high: events.filter((e) => e.severity === 'high').length,
  }), [events]);

  return (
    <div
      data-testid="audit-stream-widget"
      className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">سجل الأحداث المباشر</h3>
            <p className="text-[10px] text-slate-500">آخر 10 أحداث مالية / حوكمية</p>
          </div>
        </div>
        <button
          type="button"
          data-testid="audit-stream-refresh"
          onClick={() => setRefreshTick((t) => t + 1)}
          className="p-1.5 rounded-md hover:bg-slate-100 transition-colors"
          title="تحديث"
        >
          <RefreshCw className="w-4 h-4 text-slate-600" />
        </button>
      </div>

      {/* Filter chips + view toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100 gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {[
            { id: 'all',      label: 'الكل',  cls: 'bg-slate-800 text-white' },
            { id: 'critical', label: 'حرج',   cls: 'bg-rose-600 text-white' },
            { id: 'high',     label: 'مالي',  cls: 'bg-amber-600 text-white' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`audit-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                filter === f.id ? f.cls : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {f.label}
              {counts[f.id] > 0 && (
                <span className="ms-1 opacity-70">({counts[f.id]})</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5 bg-white rounded-md p-0.5">
          {[
            { id: 'stream', label: 'تيار' },
            { id: 'groups', label: 'حسب الكيان' },
          ].map((v) => (
            <button
              key={v.id}
              type="button"
              data-testid={`audit-view-${v.id}`}
              onClick={() => setView(v.id)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                view === v.id ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
            <RefreshCw className="w-4 h-4 animate-spin me-2" /> جارٍ التحميل…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-6">
            <AlertTriangle className="w-8 h-8 text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">لا توجد أحداث مطابقة حالياً</p>
            <p className="text-xs text-slate-400 mt-1">
              ستظهر هنا تلقائياً عند ترحيل فاتورة، تنفيذ استرداد، أو تعديل صلاحيات.
            </p>
          </div>
        )}
        {!loading && filtered.length > 0 && view === 'stream' && (
          <ul data-testid="audit-stream-list">
            {filtered.map((ev, idx) => (
              <EventRow
                key={ev.id ? `ev-${ev.id}` : `ev-${ev.ts || ''}-${ev.action || ''}-${idx}`}
                ev={ev}
                onOpen={openEntity}
              />
            ))}
          </ul>
        )}
        {!loading && filtered.length > 0 && view === 'groups' && (
          <ul data-testid="audit-stream-groups">
            {groups
              .filter((g) =>
                filter === 'all' || g.events.some((e) => e.severity === filter),
              )
              .map((g) => {
                const key = `${g.entity_type}:${g.entity_id}`;
                const open = !!expandedGroups[key];
                const EntityIcon = ENTITY_ICONS[g.entity_type] || Activity;
                return (
                  <li key={key} className="border-b border-slate-100 last:border-0">
                    <button
                      type="button"
                      data-testid={`audit-group-${key}`}
                      onClick={() => setExpandedGroups((s) => ({ ...s, [key]: !open }))}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 text-start"
                    >
                      {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                      <EntityIcon className="w-3.5 h-3.5 text-slate-500" />
                      <span className="font-semibold text-xs text-slate-800">
                        {g.entity_type || 'event'}
                      </span>
                      <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">
                        {String(g.entity_id || '').slice(0, 18)}
                        {String(g.entity_id || '').length > 18 ? '…' : ''}
                      </span>
                      <span className="ms-auto text-[10px] text-slate-500">
                        {g.events.length} حدث
                      </span>
                    </button>
                    {open && (
                      <ul className="bg-slate-50/40">
                        {g.events
                          .filter((e) => filter === 'all' || e.severity === filter)
                          .map((ev, idx) => (
                            <EventRow
                              key={ev.id ? `${key}-${ev.id}` : `${key}-${ev.ts || ''}-${idx}`}
                              ev={ev}
                              onOpen={openEntity}
                            />
                          ))}
                      </ul>
                    )}
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </div>
  );
}
